import debounce from "lodash/debounce";
import { isBreakpoint } from "../utils";

const SELECTORS = {
  videoComponent: ".js-video",
  image: ".js-image",
  playBtn: ".js-play-btn",
  playIcon: ".js-play-icon",
  pauseIcon: ".js-pause-icon",
};

const CLASSES = {
  hidden: "hidden",
  fadeIn: "fade-in",
  fadeOut: "fade-out",
};

class HomepageHero {
  constructor(context) {
    this.context = context;
    this.videoLoaded = false;
    this.videoPlayed = false;
  }

  init() {
    this.videoComponent = this.context.querySelector(SELECTORS.videoComponent);
    this.image = this.context.querySelector(SELECTORS.image);
    this.playBtn = this.context.querySelector(SELECTORS.playBtn);
    this.playIcon = this.context.querySelector(SELECTORS.playIcon);
    this.pauseIcon = this.context.querySelector(SELECTORS.pauseIcon);

    if (this.videoComponent !== null) {
      this.videoComponent.muted = true;
      this.videoComponent.loop = true;
      this.videoComponent.setAttribute("aria-hidden", "true");

      if (this.shouldLoadVideo()) {
        this.loadVideo(this.videoComponent);
      }
    }

    this.addListeners();
  }

  addListeners() {
    if (this.videoComponent !== null) {
      this.playBtn.addEventListener("click", () => this.playPauseVideo());

      this.videoComponent.addEventListener("ended", () => {
        this.playIcon.classList.remove(CLASSES.hidden);
        this.pauseIcon.classList.add(CLASSES.hidden);
      });

      this.videoComponent.addEventListener("canplaythrough", () => {
        // let cover be visible for some time and play video
        this.handlePlay(1800);
      });

      this.videoComponent.addEventListener("loadeddata", () => {
        // 'canplaythrough' is not reliable in Safari, so fallback to delayed loadeddata trigger
        this.handlePlay(3600);
      });

      window.addEventListener(
        "resize",
        debounce(() => {
          this.pause();
          this.videoPlayed = false;
          this.videoComponent.currentTime = 0;

          if (this.shouldLoadVideo()) {
            if (!this.videoLoaded) {
              this.loadVideo(this.videoComponent);
            }
            // 'canplaythrough' nor 'loadeddata' are triggered after pause in Safari
            // so rely on manual retrigger if needed
            this.handlePlay(3600);
          } else {
            this.showCover();
          }
        }),
        300,
        {
          leading: true,
          trailing: false,
        },
      );
    }
  }

  shouldLoadVideo() {
    return isBreakpoint("large");
  }

  shouldPlayVideo() {
    return this.shouldLoadVideo() && this.videoLoaded && !this.videoPlayed;
  }

  handlePlay(timeout = 0) {
    setTimeout(() => {
      if (this.shouldPlayVideo()) {
        this.play();
      }
    }, timeout);
  }

  loadVideo(element) {
    const sources = [...element.children];

    for (const source of sources) {
      if (typeof source.tagName === "string" && source.tagName === "SOURCE") {
        source.src = source.dataset.src;
      }
    }

    element.load();
    this.videoLoaded = true;
  }

  showCover() {
    this.image.classList.remove(CLASSES.fadeOut);
    this.image.classList.add(CLASSES.fadeIn);
  }

  hideCover() {
    this.image.classList.remove(CLASSES.fadeIn);
    this.image.classList.add(CLASSES.fadeOut);
  }

  play() {
    this.hideCover();

    this.videoComponent.play();
    this.playIcon.classList.add(CLASSES.hidden);
    this.pauseIcon.classList.remove(CLASSES.hidden);
    this.playIcon.setAttribute("aria-hidden", "true");
    this.pauseIcon.setAttribute("aria-hidden", "false");

    this.videoPlayed = true;
  }

  pause() {
    this.videoComponent.pause();
    this.playIcon.classList.remove(CLASSES.hidden);
    this.pauseIcon.classList.add(CLASSES.hidden);
    this.playIcon.setAttribute("aria-hidden", "false");
    this.pauseIcon.setAttribute("aria-hidden", "true");
  }

  playPauseVideo() {
    if (this.shouldLoadVideo()) {
      if (this.videoComponent.paused || this.videoComponent.currentTime === 0) {
        this.play();
      } else {
        this.pause();
      }
    } else {
      this.showCover();
    }
  }
}

export { HomepageHero };
